import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from '../utils/api';

const INITIAL_FORM = {
  name: '',
  email: '',
  phone_number: '',
  preferred_channel: 'email',
};

const normalizeText = (value) => (typeof value === 'string' ? value.trim() : '');

const derivePreferredChannel = (emailValue, phoneValue) => {
  const email = normalizeText(emailValue);
  const phoneNumber = normalizeText(phoneValue);
  if (email) return 'email';
  if (phoneNumber) return 'whatsapp';
  return 'email';
};

const BuyerInviteFriends = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadInvites = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/friend-invites');
      setInvites(Array.isArray(response.data) ? response.data : []);
    } catch (loadError) {
      setError(loadError.response?.data?.detail || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvites();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => {
      const next = {
        ...current,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'email' || name === 'phone_number') {
        next.preferred_channel = derivePreferredChannel(next.email, next.phone_number);
      }

      return next;
    });
  };

  const validateForm = () => {
    const name = normalizeText(formData.name);
    const email = normalizeText(formData.email);
    const phoneNumber = normalizeText(formData.phone_number);
    const preferredChannel = formData.preferred_channel;

    if (!name) {
      return 'Name is required';
    }

    if (!email && !phoneNumber) {
      return 'Please enter at least one contact field: Email or Phone Number.';
    }

    if (preferredChannel === 'email' && !email) {
      return 'Please enter Email for preferred channel Email.';
    }

    if ((preferredChannel === 'sms' || preferredChannel === 'whatsapp') && !phoneNumber) {
      return 'Please enter Phone Number for preferred channel SMS/WhatsApp.';
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      const name = normalizeText(formData.name);
      const email = normalizeText(formData.email);
      const phoneNumber = normalizeText(formData.phone_number);

      await axios.post('/api/friend-invites/join', {
        friend_name: name,
        friend_email: email || null,
        friend_phone_number: phoneNumber || null,
        message_type: 'Join Portal',
        preferred_channel: formData.preferred_channel,
      });

      setSuccess('Invite captured successfully.');
      setFormData(INITIAL_FORM);
      await loadInvites();
    } catch (submitError) {
      setError(submitError.response?.data?.detail || 'Failed to save invite');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={10}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <h2 className="h4 mb-3">Invite Friends</h2>
              <p className="text-muted mb-3">
                Enter details of your friends inviting them to join on portal.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="inviteName">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="inviteEmail">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="name@example.com"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="invitePhone">
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleChange}
                        placeholder="Optional if email provided"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="invitePreferredChannel">
                      <Form.Label>Preferred Channel</Form.Label>
                      <Form.Select
                        name="preferred_channel"
                        value={formData.preferred_channel}
                        onChange={handleChange}
                      >
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                        <option value="whatsapp">WhatsApp</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4} className="d-flex align-items-end">
                    <Form.Text className="text-muted mb-3">
                      Email and phone number are optional individually, but at least one is required to save.
                    </Form.Text>
                  </Col>
                </Row>

                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Invite'}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0">
            <Card.Body>
              <h3 className="h5 mb-3">Invite Log</h3>
              {loading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" />
                </div>
              ) : invites.length === 0 ? (
                <Alert variant="info" className="mb-0">No invites yet.</Alert>
              ) : (
                <Table responsive bordered hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Registered</th>
                      <th>Message Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.map((invite) => (
                      <tr key={invite.id}>
                        <td>{invite.friend_name}</td>
                        <td>{invite.friend_email || '-'}</td>
                        <td>{invite.friend_phone_number || '-'}</td>
                        <td>{invite.has_registered ? 'True' : 'False'}</td>
                        <td>{invite.message_type}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BuyerInviteFriends;
